import { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  checkinFaceUser,
  fetchSalonPurchaseHistory,
  getFaceUserProfile,
  persistFaceProfileSnapshot,
  readFaceCaptureDataUrl,
  readFacePending,
  readFaceProfileSnapshot,
  resolveFaceOrganizationId,
  resolveFaceVisitOrganizationId,
} from '../../face-identity/utils/faceIdentityApi';
import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import { fetchAdminWithRetry } from '../../admin-shared/fetchAdminWithRetry';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { recentPreviousServices } from '../../face-identity/utils/previousServicesSummary';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { buildKioskNavState } from '../../face-identity/utils/kioskNavState';
import { compressFaceThumbnail, resolveFacePortraitUrl } from '../../face-identity/utils/facePortrait';
import { SALON_GUEST_INTENT_PATH } from '../config/salonApp';
import { getSalonEntrySource } from '../utils/salonFaceSession';
import {
  markSalonFaceCheckedIn,
  clearSalonFaceSession,
  buildSalonVisitReference,
  setActiveCheckinSession,
} from '../utils/salonFaceSession';
import {
  handoffFromCheckin,
} from '../utils/visitHandoff';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import {
  getFranchiseOrganizationId,
  getFranchiseStylistId,
  franchiseAuthHeaders,
  readFranchiseFlowAuth,
} from '../../shared/franchiseFlowAuth';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/pages/FaceIdentityWelcomePage.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import { useKioskLandingConfig } from '../../face-identity/hooks/useKioskLandingConfig';
import {
  KIOSK_PAGE_KEYS,
} from '../../face-identity/utils/kioskLandingDefaults';
import { resetNaturalsSalonLuxScroll } from '../../shared/resetEnterpriseSalonLuxScroll';
import ConfirmFaceMatchPicker from '../../face-identity/components/ConfirmFaceMatchPicker';
import WelcomePreferencesCard from '../../face-identity/components/WelcomePreferencesCard';
import { useKioskRegisterForm } from '../../face-identity/hooks/useKioskRegisterForm';
import './SalonWelcomePage.css';

function formatVisitAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_) {
    return iso;
  }
}

function formatTodayPill(date = new Date()) {
  try {
    const day = date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
    const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `Today, ${day} · ${time}`;
  } catch (_) {
    return date.toLocaleString();
  }
}

function formatHistoryDate(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return String(value);
  }
}

function itemMeta(item) {
  const dateLabel = formatHistoryDate(item.date);
  const qty = item.quantity != null ? `Qty ${item.quantity}` : null;
  const sku = item.sku ? `SKU ${item.sku}` : null;
  return [qty, sku, dateLabel].filter(Boolean).join(' · ');
}

const IconSpark = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3l1.2 5.2L18 9l-4.8 1.8L12 16l-1.2-5.2L6 9l4.8-0.8L12 3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

const IconBag = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 7h12l-1 12H7L6 7z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 7V5.5A3 3 0 0 1 15 5.5V7" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconPin = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const IconCalendar = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3.5" y="5" width="17" height="15.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 3.5V7M16 3.5V7M3.5 10h17" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

function HistoryList({ items, emptyLabel, loading, previewLimit = 3, onPreviewItem, onPreviewAll }) {
  if (loading) {
    return <p className="salon-welcome-options__empty">Loading…</p>;
  }
  if (!items.length) {
    return <p className="salon-welcome-options__empty">{emptyLabel}</p>;
  }
  const visible = items.slice(0, previewLimit);
  const remaining = items.length - visible.length;
  return (
    <>
      <ul className="salon-welcome-options__list">
        {visible.map((item, idx) => {
          const meta = itemMeta(item);
          return (
            <li key={`${item.name}-${item.date || ''}-${idx}`}>
              <button
                type="button"
                className="salon-welcome-options__item salon-welcome-options__item--btn"
                onClick={() => onPreviewItem?.(item, idx)}
              >
                <span className="salon-welcome-options__item-name">{item.name}</span>
                {meta ? <span className="salon-welcome-options__item-meta">{meta}</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {remaining > 0 ? (
        <p className="salon-welcome-options__more">+{remaining} more</p>
      ) : null}
      <button
        type="button"
        className="salon-welcome-options__preview-btn"
        onClick={onPreviewAll}
      >
        Preview all
      </button>
    </>
  );
}

function HistoryPreviewModal({ open, kind, items, focusIndex, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open || focusIndex == null) return;
    const el = document.getElementById(`salon-welcome-preview-item-${focusIndex}`);
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open, focusIndex]);

  if (!open || typeof document === 'undefined') return null;

  const isTreatment = kind === 'treatments';
  const title = isTreatment ? 'Previous treatments' : 'Products purchased';
  const subtitle = isTreatment
    ? 'Treatments from your earlier salon visits'
    : 'Products you purchased on earlier visits';
  const Icon = isTreatment ? IconSpark : IconBag;

  return createPortal(
    <div className="salon-welcome-preview" role="presentation">
      <button
        type="button"
        className="salon-welcome-preview__backdrop"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div
        className="salon-welcome-preview__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="salon-welcome-preview-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="salon-welcome-preview__head">
          <div className="salon-welcome-preview__head-copy">
            <span className="salon-welcome-preview__badge" aria-hidden="true">
              <Icon />
            </span>
            <div>
              <h2 id="salon-welcome-preview-title">{title}</h2>
              <p>{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="salon-welcome-preview__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="salon-welcome-preview__body">
          {!items.length ? (
            <p className="salon-welcome-preview__empty">Nothing to show yet.</p>
          ) : (
            <ul className="salon-welcome-preview__list">
              {items.map((item, idx) => {
                const meta = itemMeta(item);
                const focused = focusIndex === idx;
                return (
                  <li
                    key={`${item.name}-${item.date || ''}-${idx}`}
                    id={`salon-welcome-preview-item-${idx}`}
                    className={`salon-welcome-preview__card${focused ? ' is-focused' : ''}`}
                  >
                    <span className="salon-welcome-preview__index" aria-hidden="true">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <div className="salon-welcome-preview__card-copy">
                      <strong>{item.name}</strong>
                      {meta ? <span>{meta}</span> : null}
                      {item.session_id ? (
                        <span className="salon-welcome-preview__session">Visit {item.session_id}</span>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="salon-welcome-preview__foot">
          <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>
          <button type="button" className="salon-welcome-preview__done" onClick={onClose}>
            Done
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

const SalonWelcomePage = () => {
  useFaceIdentityScrollShell();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const navigate = useNavigate();
  const location = useLocation();
  const pending = readFacePending();
  const stored = readFaceProfileSnapshot();

  const user = location.state?.user || stored?.user || pending?.user || null;
  const [liveUser, setLiveUser] = useState(() => user);
  const [liveProfile, setLiveProfile] = useState(
    () => location.state?.profile || stored?.profile || user?.profile || pending?.profile || null,
  );
  const guestUser = liveUser || user;
  const profile = liveProfile;
  const confidence = location.state?.confidence ?? stored?.confidence ?? pending?.confidence ?? null;
  const isNewMember = Boolean(location.state?.isNewMember || stored?.isNewMember || pending?.isNewMember);
  const [portraitUrl, setPortraitUrl] = useState(
    () =>
      resolveFacePortraitUrl(
        location.state,
        stored,
        pending,
        location.state?.user,
        stored?.user,
        pending?.user,
      ) || readFaceCaptureDataUrl() || '',
  );
  const captureUrl = portraitUrl;
  const token = location.state?.token || null;
  const deviceInfo = location.state?.deviceInfo || null;
  const nextPath = String(location.state?.next || '').trim() || SALON_GUEST_INTENT_PATH;
  const confirmCandidatesForRedirect =
    location.state?.confirmCandidates || pending?.confirmCandidates || null;

  useEffect(() => {
    if (user) return;
    if (Array.isArray(confirmCandidatesForRedirect) && confirmCandidatesForRedirect.length > 0) {
      return;
    }
    navigate(SALON_GUEST_INTENT_PATH, {
      replace: true,
      state: buildKioskNavState(location.state, {
        token,
        deviceInfo,
        next: nextPath,
        mode: 'salon',
      }),
    });
  }, [
    user,
    confirmCandidatesForRedirect,
    navigate,
    location.state,
    token,
    deviceInfo,
    nextPath,
  ]);

  const faceUserId = user?.id || stored?.faceUserId || pending?.faceUserId || null;
  const fromCustomerCode = Boolean(location.state?.fromCustomerCode);
  const fromMobileLookup = Boolean(
    location.state?.fromMobileLookup || stored?.fromMobileLookup,
  );
  const checkinCode = location.state?.checkinCode || stored?.checkinCode || null;
  const checkinId = location.state?.checkinId || stored?.checkinId || null;
  const storedEntrySource = getSalonEntrySource();
  const entrySource = fromCustomerCode
    ? 'customer_code'
    : fromMobileLookup || storedEntrySource === 'salon_mobile'
      ? 'salon_mobile'
      : storedEntrySource === 'salon_face'
        ? 'salon_face'
        : 'salon_face';

  const { config: landingConfig } = useKioskLandingConfig(KIOSK_PAGE_KEYS.SALON_ENTRY);
  const { registerForm } = useKioskRegisterForm();
  const membership = useMemo(
    () => profile?.membership || user?.membership || null,
    [profile?.membership, user?.membership],
  );
  const primaryBtnStyle = useMemo(
    () => ({
      textTransform: 'none',
      letterSpacing: '0.04em',
    }),
    [],
  );
  const [clockLabel, setClockLabel] = useState(() => formatTodayPill());

  useEffect(() => {
    const tick = () => setClockLabel(formatTodayPill());
    tick();
    const id = window.setInterval(tick, 30000);
    return () => window.clearInterval(id);
  }, []);

  const [historyLoading, setHistoryLoading] = useState(Boolean(faceUserId));
  const [treatments, setTreatments] = useState([]);
  const [products, setProducts] = useState([]);
  const [preview, setPreview] = useState({ open: false, kind: null, focusIndex: null });

  useEffect(() => {
    let cancelled = false;
    if (!faceUserId) return undefined;
    (async () => {
      try {
        const data = await getFaceUserProfile(faceUserId);
        const nextProfile = data?.profile || data?.user?.profile || null;
        if (!cancelled && nextProfile) setLiveProfile(nextProfile);
        const nextPortrait = resolveFacePortraitUrl(data?.user, data?.profile, data);
        if (!cancelled && nextPortrait) {
          setPortraitUrl((prev) => prev || nextPortrait);
        }
      } catch (_) {
        /* keep snapshot profile */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [faceUserId]);

  const openPreview = useCallback((kind, focusIndex = null) => {
    setPreview({ open: true, kind, focusIndex });
  }, []);
  const closePreview = useCallback(() => {
    setPreview({ open: false, kind: null, focusIndex: null });
  }, []);

  useEffect(() => {
    if (!faceUserId) {
      setHistoryLoading(false);
      return undefined;
    }
    let cancelled = false;
    setHistoryLoading(true);
    void fetchSalonPurchaseHistory(faceUserId, { limit: 20 })
      .then((data) => {
        if (cancelled) return;
        setTreatments(data.treatments || []);
        setProducts(data.products || []);
      })
      .catch(() => {
        if (cancelled) return;
        setTreatments([]);
        setProducts([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [faceUserId]);

  const summary = profile?.history_summary || {};
  const lastVisit = summary?.last_visit;
  const summaryLocation = lastVisit?.services_summary?.location;
  const lastVisitLocation =
    lastVisit?.franchise ||
    summary?.last_visit_location ||
    (summaryLocation && typeof summaryLocation === 'object' ? summaryLocation : null) ||
    null;
  const lastVisitCity =
    lastVisitLocation?.city || lastVisit?.city || summaryLocation?.city || '';
  const lastVisitArea =
    lastVisitLocation?.area || lastVisit?.area || summaryLocation?.area || '';
  const lastVisitCountry =
    lastVisitLocation?.country || lastVisit?.country || summaryLocation?.country || '';
  const lastVisitState =
    lastVisitLocation?.state || lastVisit?.state || summaryLocation?.state || '';
  const lastVisitAddress =
    lastVisitLocation?.address || lastVisit?.address || summaryLocation?.address || '';
  const lastVisitFranchiseName =
    lastVisitLocation?.name ||
    lastVisit?.franchise_name ||
    summaryLocation?.name ||
    summaryLocation?.franchise_name ||
    '';
  const hasLastVisitLocation = Boolean(
    lastVisitCity ||
      lastVisitArea ||
      lastVisitCountry ||
      lastVisitState ||
      lastVisitAddress ||
      lastVisitFranchiseName,
  );
  const visitCount = Number(summary.visit_count ?? 0);

  const preferences = useMemo(
    () => (profile?.preferences && typeof profile.preferences === 'object' ? profile.preferences : {}),
    [profile],
  );

  const handleCustomerInfoSaved = useCallback(
    (data) => {
      const nextUser = data?.user || guestUser;
      const nextProfile = data?.profile || profile;
      setLiveUser(nextUser);
      setLiveProfile(nextProfile);
      const snap = readFaceProfileSnapshot();
      persistFaceProfileSnapshot({
        ...snap,
        faceUserId: nextUser?.id || faceUserId,
        user: nextUser,
        profile: nextProfile,
      });
    },
    [guestUser, profile, faceUserId],
  );

  const [isContinuing, setIsContinuing] = useState(false);
  const [proceedError, setProceedError] = useState('');
  const [previousServices, setPreviousServices] = useState([]);
  const lastTakenServices = useMemo(() => {
    const fromState = location.state?.lastSelectedServices || stored?.lastSelectedServices || [];
    const recent = recentPreviousServices(
      previousServices.length ? previousServices : fromState,
      8,
    );
    return recent
      .map((item) => {
        const name = String(item.service_name || item.name || '').trim();
        if (!name) return null;
        return { id: item.id || name, name, service_name: name };
      })
      .filter(Boolean);
  }, [location.state?.lastSelectedServices, previousServices, stored?.lastSelectedServices]);

  useEffect(() => {
    let cancelled = false;
    const faceUserId = user?.id;
    if (!faceUserId) return undefined;
    (async () => {
      try {
        const base = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
        if (!base) throw new Error('no api');
        const headers = franchiseAuthHeaders({ Accept: 'application/json' });
        const res = await fetchAdminWithRetry(
          `${base}/register-services/history/${encodeURIComponent(faceUserId)}`,
          { headers, credentials: 'include', timeoutMs: 25000 },
        );
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data.items) && data.items.length) {
          if (!cancelled) setPreviousServices(data.items);
          return;
        }
        throw new Error(data.error || 'empty');
      } catch (_) {
        const visits = Array.isArray(profile?.history) ? profile.history : [];
        const fromVisits = [];
        visits.forEach((visit) => {
          const summaryPayload = visit?.services_summary;
          const selected =
            (summaryPayload && summaryPayload.selected_services) ||
            (summaryPayload && summaryPayload.services) ||
            [];
          (Array.isArray(selected) ? selected : []).forEach((item) => {
            const name = typeof item === 'string' ? item : item?.name;
            if (!name) return;
            fromVisits.push({
              id: `${visit.id || visit.visited_at}-${name}`,
              service_name: name,
              selected_at: visit.visited_at,
            });
          });
        });
        if (!cancelled) setPreviousServices(fromVisits);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, profile?.history]);

  const displayName = guestUser?.name || guestUser?.full_name || 'there';
  const initial = String(displayName).trim().charAt(0).toUpperCase() || 'N';
  const greetingTitle = isNewMember ? 'Welcome,' : 'Welcome back,';
  const sideTagline = String(landingConfig?.subtitle || '').trim()
    || (isNewMember ? "We're delighted to meet you" : "We're glad to see you again");
  const landingFooter = String(landingConfig?.footerTagline || '').trim();

  const abandonCustomerCodeQueue = async () => {
    if (!(fromCustomerCode && checkinCode)) return;
    try {
      const { releaseCheckinByCode } = await import('../../salon-admin/utils/checkinQueueApi');
      await releaseCheckinByCode(checkinCode);
    } catch (_) {
      /* non-blocking — guest is leaving anyway */
    }
  };

  const continueAfterCheckIn = async () => {
    // Service selection happens on Visit intent → Service session.
    if (isContinuing) return;
    setIsContinuing(true);
    setProceedError('');
    try {
      if (!guestUser?.id) {
        setProceedError('Guest profile missing. Please check in again.');
        return;
      }

      const snapshot = {
        faceUserId: guestUser.id,
        user: guestUser,
        profile,
        confidence,
        isNewMember,
        captureUrl,
        checkedInAt: new Date().toISOString(),
        selectedServices: [],
        lastSelectedServices: lastTakenServices,
        checkinId: checkinId || undefined,
        checkinCode: checkinCode || undefined,
        fromMobileLookup: fromMobileLookup || undefined,
      };
      persistFaceProfileSnapshot(snapshot);
      const mobile = guestUser.mobile || guestUser.phone || '';
      const display = guestUser.name || guestUser.full_name || '';
      const faceOrg =
        guestUser.organization_id ||
        guestUser.organizationId ||
        resolveFaceOrganizationId() ||
        getFranchiseOrganizationId() ||
        '';
      const visitOrg = resolveFaceVisitOrganizationId() || '';
      const faceDob =
        preferences.date_of_birth ||
        preferences.dob ||
        guestUser?.date_of_birth ||
        guestUser?.dob ||
        '';
      const faceAge =
        guestUser?.age != null && String(guestUser.age).trim() !== '' ? guestUser.age : '';
      const visitReference = buildSalonVisitReference(guestUser.id, token);

      markSalonFaceCheckedIn({
        faceUserId: guestUser.id,
        comparePrevious: !isNewMember,
        mobile,
        name: display,
        email: guestUser.email || '',
        gender: guestUser.gender || '',
        organizationId: faceOrg,
        dob: faceDob,
        age: faceAge,
        visitReference,
        entrySource,
      });

      resetNaturalsSalonLuxScroll();

      navigate(nextPath || SALON_GUEST_INTENT_PATH, {
        replace: true,
        state: {
          faceUserId: guestUser.id,
          faceProfile: snapshot,
          token,
          deviceInfo,
          comparePrevious: !isNewMember,
          visitReference,
          entrySource,
          organizationId: faceOrg || undefined,
          checkinId: checkinId || undefined,
          checkinCode: checkinCode || undefined,
          customerName: display,
          mobile,
          email: guestUser.email || '',
          gender: guestUser.gender || '',
        },
      });

      // Background tracking (non-blocking) — do not start service yet.
      void (async () => {
        if (!fromCustomerCode) {
          try {
            await checkinFaceUser(guestUser.id, {
              notes: isNewMember ? 'welcome_new' : 'welcome_return',
              services_summary: {
                selected_services: [],
                service_names: [],
                last_selected_services: lastTakenServices,
              },
              organization_id: faceOrg,
              visit_organization_id: visitOrg || undefined,
            });
          } catch (_) {
            /* ignore */
          }
        }

        try {
          if (fromCustomerCode && (checkinId || checkinCode)) {
            const { lookupCheckinByCode } = await import('../../salon-admin/utils/checkinQueueApi');
            let active = null;
            if (checkinId) {
              active = { id: checkinId, customer_code: checkinCode, status: 'active' };
            } else if (checkinCode) {
              active = await lookupCheckinByCode(checkinCode, { complete: false });
            }
            setActiveCheckinSession({
              id: active?.id || checkinId,
              code: active?.customer_code || checkinCode,
              status: active?.status || 'active',
              faceUserId: guestUser.id,
            });
            if (active) handoffFromCheckin(active);
          } else if (faceOrg) {
            const { createPartnerCheckinQueue } = await import(
              '../../salon-admin/utils/checkinQueueApi'
            );
            const faceThumb =
              (captureUrl && (await compressFaceThumbnail(captureUrl))) || captureUrl || '';
            const franchiseAuth = readFranchiseFlowAuth();
            const kioskStylistId = getFranchiseStylistId();
            const kioskStylistName =
              franchiseAuth?.full_name || franchiseAuth?.username || '';
            const created = await createPartnerCheckinQueue({
              faceUserId: guestUser.id,
              customerName: display,
              mobile,
              selectedServices: [],
              organizationId: faceOrg,
              source: entrySource,
              startService: false,
              referenceToken: visitReference,
              stylistId: kioskStylistId || undefined,
              stylistName: kioskStylistName || undefined,
              customerSnapshot: {
                name: display,
                full_name: display,
                mobile,
                email: guestUser.email || '',
                gender: guestUser.gender || '',
                age: faceAge,
                date_of_birth: faceDob,
                face_user_id: guestUser.id,
                organization_id: faceOrg,
                reference_token: visitReference,
                last_salon_reference:
                  guestUser.last_salon_reference || guestUser.lastSalonReference || '',
                profile,
                selected_services: [],
                last_selected_services: lastTakenServices,
                is_new_member: isNewMember,
                entry_source: entrySource,
                face_image: faceThumb || undefined,
                captureUrl: faceThumb || undefined,
                stylist_id: kioskStylistId || undefined,
                stylist_name: kioskStylistName || undefined,
              },
            });
            if (created?.id) {
              setActiveCheckinSession({
                id: created.id,
                code: created.customer_code,
                status: created.status || 'active',
                faceUserId: guestUser.id,
              });
              handoffFromCheckin(created);
            }
          }
        } catch (_) {
          /* ignore */
        }

        try {
          const adminBase = (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
          if (adminBase) {
            const headers = franchiseAuthHeaders({ 'Content-Type': 'application/json' });
            await fetchAdminWithRetry(`${adminBase}/nyafaz-ep/guest-session-link`, {
              method: 'POST',
              headers,
              credentials: 'include',
              timeoutMs: 25000,
              body: JSON.stringify({
                faceUserId: guestUser.id,
                mobile: mobile || undefined,
                organizationId: faceOrg || undefined,
                salonReference: visitReference,
                previousSalonReference:
                  guestUser.last_salon_reference || guestUser.lastSalonReference || undefined,
              }),
            });
          }
        } catch (_) {
          /* ignore */
        }
      })();
    } finally {
      setIsContinuing(false);
    }
  };

  const locationBlock = (
    <article className="fi-welcome__tile fi-welcome__tile--location">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconPin />
        </span>
        <h3>Last visit location</h3>
      </div>
      {hasLastVisitLocation ? (
        <div className="fi-welcome__location-fields">
          {lastVisitFranchiseName ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Salon</span>
              <span className="fi-welcome__kv-val fi-welcome__strong">{lastVisitFranchiseName}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Area</span>
            <span className="fi-welcome__kv-val">{lastVisitArea || '—'}</span>
          </div>
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">City</span>
            <span className="fi-welcome__kv-val">{lastVisitCity || '—'}</span>
          </div>
          {lastVisitState ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">State</span>
              <span className="fi-welcome__kv-val">{lastVisitState}</span>
            </div>
          ) : null}
          <div className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">Country</span>
            <span className="fi-welcome__kv-val">{lastVisitCountry || '—'}</span>
          </div>
          {lastVisitAddress && !lastVisitArea && !lastVisitCity ? (
            <div className="fi-welcome__kv">
              <span className="fi-welcome__kv-key">Address</span>
              <span className="fi-welcome__kv-val">{lastVisitAddress}</span>
            </div>
          ) : null}
        </div>
      ) : (
        <p>No franchise location recorded yet</p>
      )}
    </article>
  );

  const historyBlock = (
    <article className="fi-welcome__tile fi-welcome__history">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconCalendar />
        </span>
        <h3>Visit history</h3>
      </div>
      <div className="fi-welcome__history-body">
        <div className="fi-welcome__history-copy">
          {lastVisit?.visited_at ? (
            <p>Last visit: {formatVisitAt(lastVisit.visited_at)}</p>
          ) : (
            <p>First check-in</p>
          )}
          {hasLastVisitLocation ? (
            <p className="fi-welcome__history-location">
              {[lastVisitArea, lastVisitCity, lastVisitState, lastVisitCountry]
                .filter(Boolean)
                .join(', ') || lastVisitFranchiseName || lastVisitAddress}
            </p>
          ) : null}
        </div>
        <div className="fi-welcome__visits-ring" aria-hidden="true">
          <strong>{visitCount}</strong>
          <span>Total Visits</span>
        </div>
      </div>
    </article>
  );

  const previousServicesRecent = useMemo(
    () => recentPreviousServices(previousServices, 8),
    [previousServices],
  );

  const previousServicesBlock = (
    <article className="fi-welcome__tile fi-welcome__prev-services">
      <div className="fi-welcome__tile-head">
        <span className="fi-welcome__tile-icon">
          <IconClock />
        </span>
        <h3>Previous services</h3>
      </div>
      {previousServicesRecent.length ? (
        <ul className="fi-welcome__prev-list">
          {previousServicesRecent.map((item) => (
            <li key={item.id}>
              <strong>{item.service_name}</strong>
              <span className="fi-welcome__prev-meta">
                {item.selected_at && typeof item.selected_at === 'string'
                  ? item.selected_at.slice(0, 10)
                  : ''}
                <span aria-hidden="true">›</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p>No previous services yet</p>
      )}
    </article>
  );

  const optionsBlock = (
    <section className="salon-welcome-options" aria-label="Your previous choices">
      <header className="salon-welcome-options__head">
        <h3>Your previous choices</h3>
        <p>Preview treatments and products from earlier visits.</p>
      </header>
      <div className="salon-welcome-options__grid">
        <article className="salon-welcome-options__card">
          <div className="salon-welcome-options__card-head">
            <span className="salon-welcome-options__card-icon">
              <IconSpark />
            </span>
            <h4>Previous treatments</h4>
          </div>
          <HistoryList
            items={treatments}
            loading={historyLoading}
            emptyLabel="No previous treatments yet"
            onPreviewAll={() => openPreview('treatments')}
            onPreviewItem={(_item, idx) => openPreview('treatments', idx)}
          />
        </article>
        <article className="salon-welcome-options__card">
          <div className="salon-welcome-options__card-head">
            <span className="salon-welcome-options__card-icon">
              <IconBag />
            </span>
            <h4>Products purchased</h4>
          </div>
          <HistoryList
            items={products}
            loading={historyLoading}
            emptyLabel="No products purchased yet"
            onPreviewAll={() => openPreview('products')}
            onPreviewItem={(_item, idx) => openPreview('products', idx)}
          />
        </article>
      </div>
    </section>
  );

  const customerInfoBlock = (
    <WelcomePreferencesCard
      registerForm={registerForm}
      preferences={preferences}
      user={guestUser}
      membership={membership}
      faceUserId={faceUserId}
      onSaved={handleCustomerInfoSaved}
      heading="Customer info"
    />
  );

  const actions = (
    <div className="fi-welcome__actions">
      <button
        type="button"
        className="fi-welcome__btn fi-welcome__btn--primary"
        style={primaryBtnStyle}
        onClick={continueAfterCheckIn}
        disabled={isContinuing}
      >
        {isContinuing ? 'Saving…' : 'Proceed'}
        <span aria-hidden="true">→</span>
      </button>
    </div>
  );

  const confirmCandidates = location.state?.confirmCandidates || pending?.confirmCandidates || null;

  if (Array.isArray(confirmCandidates) && confirmCandidates.length > 0 && !user) {
    return (
      <div className="fi-welcome fi-scroll-page salon-welcome-page page-bg fi-lux-theme">
        <main className="fi-welcome__empty fi-welcome__empty--confirm">
          <ConfirmFaceMatchPicker
            candidates={confirmCandidates}
            message={location.state?.confirmMessage || pending?.confirmMessage}
            captureUrl={captureUrl}
            onConfirmed={(snapshot) => {
              persistFaceProfileSnapshot(snapshot);
              navigate('/nyafaz-ep/salon/welcome', {
                replace: true,
                state: {
                  ...location.state,
                  user: snapshot.user,
                  profile: snapshot.profile,
                  confidence: snapshot.confidence,
                  captureUrl: snapshot.captureUrl,
                  confirmCandidates: undefined,
                  confirmMessage: undefined,
                },
              });
            }}
            onScanAgain={() =>
              navigate('/nyafaz-ep/salon/scan', {
                replace: true,
                state: buildKioskNavState(location.state, {
                  token,
                  deviceInfo,
                  next: nextPath,
                  mode: 'salon',
                  confirmCandidates: undefined,
                }),
              })
            }
            onUseMobile={() =>
              navigate('/nyafaz-ep/salon/mobile', {
                replace: true,
                state: buildKioskNavState(location.state, {
                  token,
                  deviceInfo,
                  next: nextPath,
                  mode: 'salon',
                  fromFaceUnknown: true,
                  captureUrl,
                  confirmCandidates: undefined,
                  confirmMessage: undefined,
                }),
              })
            }
          />
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fi-welcome fi-scroll-page salon-welcome-page page-bg fi-lux-theme">
        <main className="fi-welcome__empty">
          <h1>No guest profile found</h1>
          <p>Continue with face capture, mobile number, or a customer code.</p>
          <button
            type="button"
            className="fi-welcome__btn fi-welcome__btn--primary"
            style={primaryBtnStyle}
            onClick={() =>
              navigate(SALON_GUEST_INTENT_PATH, {
                replace: true,
                state: buildKioskNavState(location.state, {
                  token,
                  deviceInfo,
                  next: nextPath,
                  mode: 'salon',
                }),
              })
            }
          >
            Back to start
          </button>
        </main>
      </div>
    );
  }

  const previewItems = preview.kind === 'treatments' ? treatments : products;

  return (
    <div className="fi-welcome fi-scroll-page salon-welcome-page page-bg fi-lux-theme">
      <div className="fi-welcome__stage-shell">
        <header className="fi-welcome__top">
          <div className="fi-welcome__top-row">
            <div className="fi-welcome__brand">
              <FaceIdentityBrandLogo
                showLogo={showLogo}
                orgLogoUrl={orgLogoUrl}
                onLogoError={onLogoError}
                imgClassName="fi-welcome__logo"
                fallbackClassName="fi-welcome__logo-text"
              />
            </div>
            <FaceIdentityFlowNav
              onBack={async () => {
                await abandonCustomerCodeQueue();
                clearSalonFaceSession();
                const backPath = fromCustomerCode
                  ? '/nyafaz-ep/salon/customer-code'
                  : fromMobileLookup
                    ? '/nyafaz-ep/salon/mobile'
                    : '/nyafaz-ep/salon/scan';
                navigate(backPath, {
                  replace: true,
                  state: buildKioskNavState(location.state, {
                    token,
                    deviceInfo,
                    next: nextPath,
                    mode: 'salon',
                  }),
                });
              }}
              onHome={() =>
                navigate(resolveSalonHomePath(), { replace: true })
              }
              homePath={resolveSalonHomePath()}
            />
          </div>
          <div className="fi-welcome__clock" aria-live="polite">
            <IconCalendar />
            <span>{clockLabel}</span>
          </div>
        </header>

        <section className="fi-welcome__greeting" aria-label="Welcome greeting">
          <h1 className="fi-welcome__greeting-title">
            {greetingTitle} <em>{displayName}</em>
          </h1>
          <p className="fi-welcome__greeting-sub">{sideTagline}</p>
        </section>

        <main className="fi-welcome__stage">
          <aside className="fi-welcome__portrait">
            <div className="fi-welcome__photo">
              {captureUrl ? (
                <img src={captureUrl} alt={displayName} />
              ) : (
                <div className="fi-welcome__photo-fallback" aria-hidden="true">
                  {initial}
                </div>
              )}
            </div>
          </aside>

          <div className="fi-welcome__info-grid">
            {customerInfoBlock}
            {locationBlock}
            {historyBlock}
          </div>

          <div className="fi-welcome__main-stack">
            {proceedError ? <p className="fi-welcome__error">{proceedError}</p> : null}

            {previousServicesBlock}
            {optionsBlock}
            {actions}
          </div>
        </main>

        {landingFooter ? (
          <footer className="fi-welcome__footer">
            <p>{landingFooter}</p>
          </footer>
        ) : null}
      </div>

      <HistoryPreviewModal
        open={preview.open}
        kind={preview.kind}
        items={previewItems}
        focusIndex={preview.focusIndex}
        onClose={closePreview}
      />
    </div>
  );
};

export default SalonWelcomePage;
